import { useState } from 'react';
import AdminPage from '../components/AdminPage';
import List from '../components/List';
import Button from '../components/Button';
import Modal from '../components/Modal';
import TextInput from '../components/input/TextInput';
import TextAreaInput from '../components/input/TextAreaInput';
import { useGetQuizzesQuery } from './queries/getQuizzesQuery';
import { useCreateQuizMutation } from './mutations/createQuizMutation';
import { useGetClientsQuery } from './queries/getClientsQuery';
import { completitionEmailOptions } from '../common';
import { format } from 'date-fns';


const defaultIntroduction =
    'Welcome to the Culturesphere™ Culture Assessment – Individual Survey\n\n' +
    'Culturesphere™ is an insights tool designed to uncover the cultural influences and expectations active inside your team.\n\n' +
    'This survey has two parts. It takes approximately 10 minutes to complete. We encourage you to take as much time as you need to accurately complete each section.\n';

const defaultIntroductionLeader =
    'Welcome to the Culturesphere™ Culture Assessment – Leader Survey\n\n' +
    'Culturesphere™ is an insights tool designed to uncover the cultural influences and expectations active inside your team.\n\n' +
    'This survey takes approximately 5 minutes to complete. We encourage you to take as much time as you need to accurately complete each section.\n';

export function AdminSurveysView() {
    const { data, refetch } = useGetQuizzesQuery();
    const { data: clientsData } = useGetClientsQuery();

    const [showCreateSurveyModal, setShowCreateSurveyModal] = useState(false);
    const [title, setTitle] = useState('');
    const [teamName, setTeamName] = useState('');
    const [introduction, setIntroduction] = useState(defaultIntroduction);
    const [introductionLeader, setIntroductionLeader] = useState(defaultIntroductionLeader);
    const [sendEmailMode, setSendEmailMode] = useState('on_demand');
    const [clientId, setClientId] = useState('');

    const [createQuiz, { loading }] = useCreateQuizMutation({
        variables: {
            input: {
                title,
                introduction,
                introductionLeader,
                teamName,
                client: clientId,
                template: '678c72be-32a3-47cd-970b-69c9c4ca05b6', // może później dynamiczne
                sendCompletionEmailMode: sendEmailMode,
            },
        },
        onCompleted: () => {
            refetch();

            setTitle('');
            setTeamName('');
            setIntroduction(defaultIntroduction);
            setIntroductionLeader(defaultIntroductionLeader);
            setSendEmailMode('on_demand');
            setClientId('');
            setShowCreateSurveyModal(false);
        },
    });

    if (!data) {
        return <p>Loading&hellip;</p>;
    }

    const openSurveys = data.quizzes.filter((survey) => !survey.closed);
    const closedSurveys = data.quizzes.filter((survey) => survey.closed);

    return (
        <AdminPage
            title="Surveys"
            actions={
                <Button onClick={() => setShowCreateSurveyModal(true)}>
                    Create Survey
                </Button>
            }
        >
            <h2 className="text-lg font-bold mt-4 mb-2">Open Surveys</h2>
            <List
                items={openSurveys}
                render={(survey) => (
                    <div className="leading-tight flex items-center justify-between">
                        <div>
                            <p>
                                {survey.title}{' '}
                                {survey.closed && (
                                    <span className="text-red-500 text-sm">(Closed)</span>
                                )}
                            </p>
                            <p>
                                <small className="text-gray-600">{survey.client.name}</small>
                            </p>
                        </div>
                        <span className="text-sm text-gray-500 whitespace-nowrap ml-4">
                            {format(new Date(survey.created), 'dd MMM yyyy')}
                        </span>
                    </div>
                )}
                link={(quiz) => `/admin/surveys/${quiz.id}`}
            />

            <h2 className="text-lg font-bold mt-6 mb-2">Closed Surveys</h2>
            <List
                items={closedSurveys}
                render={(survey) => (
                    <div className="leading-tight flex items-center justify-between">
                        <div>
                            <p>
                                {survey.title}{' '}
                                {survey.closed && (
                                    <span className="text-red-500 text-sm">(Closed)</span>
                                )}
                            </p>
                            <p>
                                <small className="text-gray-600">{survey.client.name}</small>
                            </p>
                        </div>
                        <span className="text-sm text-gray-500 whitespace-nowrap ml-4">
                            {format(new Date(survey.created), 'dd MMM yyyy')}
                        </span>
                    </div>
                )}
                link={(quiz) => `/admin/surveys/${quiz.id}`}
            />

            {showCreateSurveyModal && (
                <Modal
                    heading="Create Survey"
                    onClose={() => setShowCreateSurveyModal(false)}
                >
                    <div className="space-y-3">
                        <TextInput
                            placeholder="Title"
                            value={title}
                            onChange={setTitle}
                        />

                        <TextInput
                            placeholder="Team Name"
                            value={teamName}
                            onChange={setTeamName}
                        />

                        <div>
                            <label>Client</label>
                            <select
                                value={clientId}
                                onChange={(e) => setClientId(e.target.value)}
                                className="w-full border border-gray-300 rounded px-2 py-1"
                            >
                                <option value="">-- Select Client --</option>
                                {clientsData?.clients.map((client) => (
                                    <option key={client.id} value={client.id}>
                                        {client.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label>Introduction for individuals</label>
                            <TextAreaInput
                                placeholder="Introduction for individuals"
                                value={introduction}
                                onChange={setIntroduction}
                            />
                        </div>

                        <div>
                            <label>Introduction for leaders</label>
                            <TextAreaInput
                                placeholder="Introduction for leaders"
                                value={introductionLeader}
                                onChange={setIntroductionLeader}
                            />
                        </div>

                        <div className="hidden">
                            <label>When should individual PDF reports be sent?</label>
                            {completitionEmailOptions.map((option) => (
                                <div key={option.value} className="flex items-center gap-2">
                                    <input
                                        type="radio"
                                        id={option.value}
                                        name="email_mode"
                                        value={option.value}
                                        checked={sendEmailMode === option.value}
                                        onChange={() => setSendEmailMode(option.value)}
                                    />
                                    <label htmlFor={option.value}>{option.label}</label>
                                </div>
                            ))}
                        </div>

                        <Button onClick={createQuiz} loading={loading}>
                            Create
                        </Button>
                    </div>
                </Modal>
            )}
        </AdminPage>
    );
}